<script setup lang="ts">
import EsLogo2024 from '~/components/EsLogo2024.vue';
import InfoMessage from '~/components/InfoMessage.vue';
import LoadingSpinner from '~/components/LoadingSpinner.vue';
import { useAppContext } from '~/services/AppContext';
import useStaffUserStore from '~/store/staffUser';
import { asApiError } from '~/utils/apiErrors';
import { flashToken, loginUrl, redirectTo, tokenExpired } from '~/utils/flashAuth';

/**
 * Only EnergySage staff use the console (ADR-0006). Before any page loads, the service confirms who is signed in:
 * no one is sent to the Flash sign-in, and anyone but staff is told the console isn't for them. The service enforces
 * this on every request; this only spares everyone else a page that can't load. On a laptop there is no Flash
 * (`ACCOUNTS_DOMAIN` is unset) and the service answers as its local staff user.
 */
type Access = 'checking' | 'staff' | 'signing-in' | 'not-staff' | 'failed';

const { systemDesignService } = useAppContext();
const staffUserStore = useStaffUserStore();
/** A page that needs the whole width (the label editor) asks for it with `definePageMeta({ fluid: true })`. */
const route = useRoute();
const fluid = computed(() => route.meta.fluid === true);
const accountsDomain: string = useRuntimeConfig().public.ACCOUNTS_DOMAIN ?? '';
const access = ref<Access>('checking');
const problem = ref('');
let latestCheck = 0;

function signIn() {
    access.value = 'signing-in';
    redirectTo(loginUrl(accountsDomain, window.location.href));
}

async function check() {
    const thisCheck = ++latestCheck;
    access.value = 'checking';
    const token = accountsDomain ? flashToken() : null;
    if (accountsDomain && (!token || tokenExpired(token))) {
        signIn();
        return;
    }
    try {
        const me = await systemDesignService.getMe();
        if (thisCheck !== latestCheck) return; // a later check decides
        staffUserStore.set(me.id);
        access.value = 'staff';
    } catch (failure) {
        if (thisCheck !== latestCheck) return;
        const error = asApiError(failure);
        if (error.status === 401 && accountsDomain) {
            signIn();
            return;
        }
        access.value = error.status === 403 ? 'not-staff' : 'failed';
        problem.value = error.message;
    }
}

check();
</script>

<template>
    <div class="body-container d-flex flex-column min-vh-100">
        <header class="console-header">
            <nuxt-link
                to="/"
                class="console-home"
                aria-label="Design console home">
                <es-logo-2024
                    width="100%"
                    height="auto" />
            </nuxt-link>
            <span class="console-name">Design console</span>
            <nav
                v-if="access === 'staff'"
                class="console-nav"
                aria-label="Console">
                <nuxt-link
                    to="/labels"
                    class="labels-link">
                    Labels
                </nuxt-link>
                <nuxt-link
                    to="/shadow"
                    class="shadow-link">
                    Shadow mode
                </nuxt-link>
                <nuxt-link
                    to="/"
                    class="new-design">
                    New Design
                </nuxt-link>
            </nav>
        </header>

        <div
            v-if="access !== 'staff'"
            class="content-container flex-grow-1"
            role="main">
            <es-container class="py-400 py-lg-500">
                <section
                    v-if="access === 'checking' || access === 'signing-in'"
                    class="loading-container">
                    <loading-spinner :height="64">
                        <span>{{ access === 'checking' ? 'Checking your sign-in' : 'Signing you in' }}</span>
                    </loading-spinner>
                </section>
                <info-message
                    v-else-if="access === 'not-staff'"
                    type="error"
                    class="access-message not-staff">
                    Only EnergySage staff can use the Design console. If you work at EnergySage, sign in with your
                    staff account.
                </info-message>
                <div
                    v-else
                    class="access-message access-failed">
                    <info-message type="error">{{ problem }}</info-message>
                    <es-button
                        class="retry-access mt-200"
                        size="sm"
                        @click="check()">
                        Try again
                    </es-button>
                </div>
            </es-container>
        </div>

        <suspense v-else>
            <div
                class="content-container flex-grow-1"
                role="main">
                <es-container
                    :fluid="fluid"
                    class="py-400 py-lg-500">
                    <slot />
                </es-container>
            </div>

            <template #fallback>
                <section class="loading-container">
                    <loading-spinner :height="64">
                        <span>Loading</span>
                    </loading-spinner>
                </section>
            </template>
        </suspense>
    </div>
</template>

<style scoped lang="scss">
@use '@energysage/es-ds-styles/scss/variables';

.console-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px;
    padding: 12px 24px;
    background-color: variables.$white;
    border-bottom: 1px solid variables.$gray-200;
}

.console-home {
    display: flex;
    flex: 0 0 140px;
    color: inherit;
}

.console-name {
    padding-left: 16px;
    border-left: 1px solid variables.$gray-300;
    font-weight: 700;
    white-space: nowrap;
}

.console-nav {
    display: flex;
    gap: 24px;
    margin-left: auto;
}

.labels-link,
.shadow-link,
.new-design {
    font-weight: 700;
    white-space: nowrap;
}

.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
}

@media (max-width: 575.98px) {
    .console-header {
        padding: 12px 16px;
        gap: 12px;
    }

    .console-home {
        flex-basis: 100px;
    }

    .console-name {
        padding-left: 12px;
    }

    .console-name,
    .labels-link,
    .shadow-link,
    .new-design {
        font-size: 14px;
    }

    .console-nav {
        gap: 16px;
    }
}
</style>
